import {
  BrowserLiveMedia,
  type LiveBrowserTranscript,
} from '../../../../vendor/agentplace-voice/browser-live-media.ts';
/** Browser microphone, native Live audio, and the configured legacy voice transport. */
import {
  LiveConnectionRecovery,
  isRetryableVoiceClose,
} from '../../../../vendor/agentplace-voice/live-connection-recovery.ts';
import { buildSessionWsUrl, getWsBaseUrl } from './api-url.ts';
import { claimFirstVoiceOpen } from './voice-greeting-ledger.ts';
import { AgentAuth } from '../agent-auth.ts';
import type { BrowserVoiceScreenSelection, MemoryEntry } from '../../../../../shared';
import { isRecord } from '../util/type-guards.ts';
import { VoiceRealtimePlaybackQueue } from './voice-realtime-playback-queue.ts';
import { VoiceScreenSelectionTracker } from './voice-screen-selection.ts';
import type { AttachmentLocale } from '../../../../../shared/localization.ts';
import {
  readVisitorContextUpdate,
  type VisitorContextUpdate,
} from '../../../../../shared/visitor-context.ts';

const INPUT_SAMPLE_RATE = 24_000;
const LIVE_PLAYBACK_BUFFER_SECONDS = 0.15;

const CAPTURE_WORKLET = `
class VoiceCapture extends AudioWorkletProcessor {
  constructor() { super(); this.frame = new Float32Array(480); this.offset = 0; }
  process(inputs) {
    const channel = inputs[0]?.[0];
    if (channel) for (const sample of channel) {
      this.frame[this.offset++] = sample;
      if (this.offset === this.frame.length) {
        this.port.postMessage(this.frame);
        this.frame = new Float32Array(480);
        this.offset = 0;
      }
    }
    return true;
  }
}
registerProcessor('voice-capture', VoiceCapture);
`;

function floatTo16BitBase64(samples: Float32Array): string {
  const buffer = new ArrayBuffer(samples.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** The client's one PCM decode: 24 kHz PCM16 base64 into playback samples.
 *  Exported so wire-contract tests can drive the real conversion the playback
 *  clock (and therefore every truncation millisecond) is derived from. */
export function base64ToFloat32(base64: string): Float32Array<ArrayBuffer> {
  const binary = atob(base64);
  if (binary.length === 0 || binary.length % 2 !== 0) {
    throw new Error('Invalid voice audio.');
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const view = new DataView(bytes.buffer);
  const out = new Float32Array(bytes.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = view.getInt16(i * 2, true) / 0x8000;
  }
  return out;
}

// Noise gate: chunks quieter than this RMS are sent as silence so background
// noise (typing, breathing) never reads as speech onset to the server VAD.
const NOISE_GATE_RMS = 0.025;
// Keep the gate open this long after the last loud chunk so word tails and
// short intra-word pauses are not clipped.
const NOISE_GATE_HANGOVER_MS = 400;

function chunkRms(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    sum += samples[i] * samples[i];
  }
  return Math.sqrt(sum / samples.length);
}

/** Live levels (0..1) for the waveform, written by the session's meter loop.
 *  A plain mutable bus read inside the canvas RAF — never React state. */
export const realtimeVoiceLevels = { input: 0, output: 0 };

function analyserRms(analyser: AnalyserNode): number {
  const data = new Float32Array(analyser.fftSize);
  analyser.getFloatTimeDomainData(data);
  return chunkRms(data);
}

/** Mic capture (PCM16/24kHz chunks) + gapless playback queue with barge-in clear. */
export class VoiceRealtimeAudio {
  #context: AudioContext | null = null;
  #stream: MediaStream | null = null;
  #playbackTime = 0;
  readonly #playback: VoiceRealtimePlaybackQueue;
  #analyser: AnalyserNode | null = null;
  #playbackAnalyser: AnalyserNode | null = null;
  #playbackSink: MediaStreamAudioDestinationNode | null = null;
  #playbackElement: HTMLAudioElement | null = null;
  #muted = false;
  #gateOpenUntil = 0;
  #continuous = false;
  #generation = 0;

  constructor(options?: { onPlaybackComplete?: (itemId: string) => void }) {
    this.#playback = new VoiceRealtimePlaybackQueue(options?.onPlaybackComplete ?? (() => {}));
  }

  /** Live keeps silence on the input clock; legacy voice uses server noise reduction. */
  setContinuous(continuous: boolean): void {
    this.#continuous = continuous;
  }

  async start(onChunk: (base64Pcm: string) => void): Promise<void> {
    const generation = ++this.#generation;
    this.#context = new AudioContext({ sampleRate: INPUT_SAMPLE_RATE });
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: this.#continuous,
        autoGainControl: true,
        channelCount: 1,
      },
    });
    if (generation !== this.#generation) {
      for (const track of stream.getTracks()) track.stop();
      return;
    }
    this.#stream = stream;
    const workletUrl = URL.createObjectURL(
      new Blob([CAPTURE_WORKLET], { type: 'text/javascript' }),
    );
    try {
      await this.#context.audioWorklet.addModule(workletUrl);
    } finally {
      URL.revokeObjectURL(workletUrl);
    }
    if (generation !== this.#generation || !this.#context) return;
    const source = this.#context.createMediaStreamSource(this.#stream);
    this.#analyser = this.#context.createAnalyser();
    this.#analyser.fftSize = 256;
    source.connect(this.#analyser);
    this.#playbackAnalyser = this.#context.createAnalyser();
    this.#playbackAnalyser.fftSize = 256;
    this.#wirePlayout(this.#context, this.#playbackAnalyser);
    const capture = new AudioWorkletNode(this.#context, 'voice-capture');
    source.connect(capture);
    capture.port.onmessage = (ev: MessageEvent<Float32Array>) => {
      if (generation !== this.#generation) return;
      if (this.#continuous) {
        onChunk(floatTo16BitBase64(this.#muted ? new Float32Array(ev.data.length) : ev.data));
      } else if (!this.#muted) {
        onChunk(floatTo16BitBase64(this.#applyNoiseGate(ev.data)));
      }
    };
  }

  /** Replace sub-threshold chunks with silence (hangover keeps word tails intact). */
  #applyNoiseGate(samples: Float32Array): Float32Array {
    const now = performance.now();
    if (chunkRms(samples) >= NOISE_GATE_RMS) {
      this.#gateOpenUntil = now + NOISE_GATE_HANGOVER_MS;
      return samples;
    }
    if (now < this.#gateOpenUntil) {
      return samples;
    }
    return new Float32Array(samples.length);
  }

  /** RMS level 0..1 for the waveform accent; 0 while muted so the UI never suggests the mic is heard. */
  level(): number {
    if (!this.#analyser || this.#muted) {
      return 0;
    }
    return analyserRms(this.#analyser);
  }

  playbackLevel(): number {
    return this.#playbackAnalyser ? analyserRms(this.#playbackAnalyser) : 0;
  }

  setMuted(muted: boolean): void {
    this.#muted = muted;
  }

  enqueuePlayback(itemId: string, contentIndex: number, base64Pcm: string): void {
    if (!this.#context) {
      return;
    }
    const samples = base64ToFloat32(base64Pcm);
    const now = this.#context.currentTime;
    let startAt = Math.max(now, this.#playbackTime);
    if (this.#continuous && this.#playbackTime <= now) {
      startAt = now + LIVE_PLAYBACK_BUFFER_SECONDS;
    }
    const buffer = this.#context.createBuffer(1, samples.length, INPUT_SAMPLE_RATE);
    buffer.copyToChannel(samples, 0);
    const source = this.#context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.#playbackAnalyser ?? this.#context.destination);
    source.start(startAt);
    this.#playbackTime = startAt + buffer.duration;
    this.#playback.enqueue(itemId, contentIndex, startAt, this.#playbackTime, source);
    source.onended = () => {
      this.#playback.sourceEnded(itemId, source);
    };
  }

  markPlaybackInputDone(itemId: string): void {
    this.#playback.markInputDone(itemId);
  }

  hasPlayback(): boolean {
    return this.#playback.hasPlayback();
  }

  /** Barge-in: stop everything queued and classify every affected conversation item. */
  clearPlayback(): {
    completedItemIds: string[];
    truncations: Array<{ itemId: string; contentIndex: number; audioEndMs: number }>;
  } {
    const now = this.#context?.currentTime ?? 0;
    const interruption = this.#playback.clear(now);
    this.#playbackTime = now;
    return interruption;
  }

  async stop(): Promise<void> {
    this.#generation++;
    this.clearPlayback();
    for (const track of this.#stream?.getTracks() ?? []) {
      track.stop();
    }
    this.#playbackElement?.pause();
    if (this.#playbackElement) {
      this.#playbackElement.srcObject = null;
    }
    this.#playbackElement = null;
    this.#playbackSink = null;
    const context = this.#context;
    this.#context = null;
    this.#stream = null;
    this.#analyser = null;
    this.#playbackAnalyser = null;
    await context?.close();
  }

  /**
   * The model's voice must play through a media element, not
   * `AudioContext.destination`: Chrome's echo canceller subtracts only
   * element/WebRTC playout from the mic signal, so WebAudio-rendered speech
   * comes back in as caller audio — the model barges in on its own echo and
   * answers a mis-transcription of itself. If the element refuses to play
   * (autoplay policy — no user gesture in the call chain), fall back to the
   * direct output: audible without echo cancellation beats silent.
   */
  #wirePlayout(context: AudioContext, analyser: AnalyserNode): void {
    const sink = context.createMediaStreamDestination();
    analyser.connect(sink);
    const element = new Audio();
    element.srcObject = sink.stream;
    this.#playbackSink = sink;
    this.#playbackElement = element;
    void element.play().catch(() => {
      if (this.#context !== context) return;
      analyser.disconnect();
      analyser.connect(context.destination);
      element.srcObject = null;
      this.#playbackElement = null;
      this.#playbackSink = null;
    });
  }
}

export type VoiceRealtimeState =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'building';

export interface VoiceRealtimeSessionOptions {
  getWebsiteContext?: () => Readonly<Record<string, string | number | boolean | null>> | undefined;
  getAgentSessionId: () => string | null;
  getAttachmentLocale: () => AttachmentLocale;
  screenSelection: BrowserVoiceScreenSelection;
  /** Read the current visitor memory bank on every connection, including recovery. */
  getMemories?: () => MemoryEntry[];
  onState?: (state: VoiceRealtimeState) => void;
  /** Live supplies display boundaries from transcript timestamps, independent of audio levels. */
  onAssistantCaption?: (delta: string, startsNewCaption?: boolean) => void;
  onUserCaption?: (delta: string) => void;
  /** Apply a requested update and return the actual bank, including on storage failure. */
  onMemory?: (update: VisitorContextUpdate) => MemoryEntry[];
  onError?: (message: string) => void;
  onClose?: () => void;
  /** Delegated work pending (true) / all done (false). Only admission includes response identity. */
  onRunBusy?: (busy: boolean, acceptedResponseId?: string) => void;
}

/**
 * `?phone_sim=1` on the PAGE turns this browser into a faked telephone caller:
 * the flag rides to `/voice`, where the gateway swaps in the phone persona,
 * a fresh request-scoped session and the screenless run presentation. Deliberately
 * not a UI control — it is a team-testing lever for the phone fast path, not a
 * visitor-facing mode.
 */
function phoneSimRequested(): boolean {
  return new URLSearchParams(window.location.search).get('phone_sim') === '1';
}

/** Binds browser audio after the gateway declares its protocol and prepares session context. */
export class VoiceRealtimeSession {
  #options: VoiceRealtimeSessionOptions;
  readonly #voiceConnectionId = crypto.randomUUID();
  #agentSessionId: string | null = null;
  #socket: WebSocket | null = null;
  #audio: VoiceRealtimeAudio;
  #media: BrowserLiveMedia | null = null;
  #stopped = false;
  #stopPromise: Promise<void> | undefined;
  #active = false;
  #muted = false;
  #terminal = false;
  #reconnecting = false;
  #livenessTimer: ReturnType<typeof setTimeout> | undefined;
  #activationTimer: ReturnType<typeof setTimeout> | undefined;
  readonly #recovery: LiveConnectionRecovery;
  #audioPreparation: Promise<void> | null = null;
  #activationSent = false;
  /** Whether this session requested the page load's first-open greeting. */
  #greetClaimed = false;
  readonly #screenSelection: VoiceScreenSelectionTracker;
  #meterId: number | null = null;
  #resolveStart: (() => void) | null = null;
  #rejectStart: ((error: Error) => void) | null = null;
  #liveProtocol = false;
  #continuousSpeaking = false;
  #lastSequence = 0;
  #assistantCaptionEndMs = -1;

  constructor(options: VoiceRealtimeSessionOptions) {
    this.#options = options;
    this.#recovery = new LiveConnectionRecovery(
      () => this.#connect(),
      () => {
        this.#options.onError?.('Voice could not reconnect. Please try again.');
        void this.stop();
      },
    );
    this.#screenSelection = new VoiceScreenSelectionTracker(options.screenSelection);
    this.#audio = new VoiceRealtimeAudio({
      onPlaybackComplete: (itemId) => {
        if (this.#liveProtocol) return;
        this.#sendControlEvent({ type: 'voice.playback.completed', itemId });
        if (!this.#audio.hasPlayback()) {
          this.#options.onState?.('listening');
        }
      },
    });
  }

  async start(): Promise<void> {
    this.#options.onState?.('connecting');
    this.#agentSessionId = this.#options.getAgentSessionId();
    const active = new Promise<void>((resolve, reject) => {
      this.#resolveStart = resolve;
      this.#rejectStart = reject;
    });
    this.#connect();
    await active;
  }

  #connect(): void {
    if (this.#stopped) return;
    this.#terminal = false;
    this.#activationSent = false;
    const phoneSimulation = phoneSimRequested();
    const socket = new WebSocket(
      buildSessionWsUrl({
        baseUrl: getWsBaseUrl(),
        path: '/voice',
        agentSessionId: this.#agentSessionId,
        locale: phoneSimulation ? undefined : this.#options.getAttachmentLocale(),
        phoneSimulation,
        voiceTransport: 'webrtc',
        voiceConnectionId: this.#voiceConnectionId,
      }),
    );
    this.#socket = socket;
    clearTimeout(this.#activationTimer);
    this.#activationTimer = setTimeout(() => {
      if (this.#socket !== socket) return;
      if (this.#liveProtocol) this.#reconnect();
      else {
        this.#rejectStart?.(new Error('Voice setup timed out.'));
        void this.stop();
      }
    }, 30_000);
    socket.addEventListener('open', () => {
      if (this.#socket === socket) this.#sendInitialize(socket);
    });
    socket.addEventListener('message', (ev) => {
      if (this.#socket === socket && typeof ev.data === 'string') {
        this.#handleServerEvent(ev.data);
        if (this.#active && this.#liveProtocol) {
          clearTimeout(this.#livenessTimer);
          this.#livenessTimer = setTimeout(() => this.#reconnect(), 15_000);
        }
      }
    });
    socket.addEventListener('close', (event) => {
      if (this.#socket !== socket) return;
      if (this.#liveProtocol && !this.#terminal && isRetryableVoiceClose(event.code)) {
        this.#reconnect();
      } else {
        void this.stop();
      }
    });
    socket.addEventListener('error', () => {
      if (this.#socket !== socket || this.#liveProtocol) return;
      this.#options.onError?.('voice connection failed');
      this.#rejectStart?.(new Error('voice connection failed'));
      void this.stop();
    });
  }

  #reconnect(): void {
    if (this.#stopped) return;
    if (this.#terminal) {
      void this.stop();
      return;
    }
    this.#reconnecting = true;
    this.#active = false;
    clearTimeout(this.#livenessTimer);
    clearTimeout(this.#activationTimer);
    const socket = this.#socket;
    this.#socket = null;
    socket?.close();
    this.#audio.clearPlayback();
    if (this.#media) {
      this.#media.disconnect();
      this.#audioPreparation = null;
    }
    this.#continuousSpeaking = false;
    this.#lastSequence = 0;
    this.#assistantCaptionEndMs = -1;
    this.#options.onState?.('connecting');
    this.#recovery.retry();
  }

  #startMeter(): void {
    if (this.#meterId !== null) return;
    const meter = () => {
      if (!this.#active) {
        realtimeVoiceLevels.input = 0;
        realtimeVoiceLevels.output = 0;
        this.#meterId = requestAnimationFrame(meter);
        return;
      }
      realtimeVoiceLevels.input = this.#media ? this.#media.inputLevel : this.#audio.level();
      realtimeVoiceLevels.output = this.#media
        ? this.#media.outputLevel
        : this.#audio.playbackLevel();
      const speaking = realtimeVoiceLevels.output > 0.005;
      if (this.#liveProtocol && speaking !== this.#continuousSpeaking) {
        this.#continuousSpeaking = speaking;
        this.#options.onState?.(speaking ? 'speaking' : 'listening');
      }
      this.#meterId = requestAnimationFrame(meter);
    };
    this.#meterId = requestAnimationFrame(meter);
  }

  async #prepareAudio(socket: WebSocket): Promise<void> {
    if (this.#stopped || this.#activationSent) return;
    try {
      if (this.#media) {
        if (this.#audioPreparation) return;
        const media = this.#media;
        this.#audioPreparation = (async () => {
          const sdp = await media.createOffer();
          if (this.#socket !== socket || this.#stopped) return;
          media.setMuted(this.#muted);
          socket.send(
            JSON.stringify({
              ...this.#screenSelection.activate(),
              websiteContext: this.#options.getWebsiteContext?.(),
              type: 'voice.webrtc.offer',
              sdp,
            }),
          );
        })();
        await this.#audioPreparation;
        return;
      }
      this.#audioPreparation ??= this.#audio.start((base64Pcm) => {
        if (this.#active && this.#socket?.readyState === WebSocket.OPEN) {
          this.#socket.send(
            JSON.stringify({
              type: this.#liveProtocol ? 'voice.audio' : 'input_audio_buffer.append',
              audio: base64Pcm,
            }),
          );
        }
      });
      await this.#audioPreparation;
      if (
        this.#stopped ||
        this.#socket !== socket ||
        socket.readyState !== WebSocket.OPEN ||
        this.#activationSent
      )
        return;
      this.#activationSent = true;
      this.#greetClaimed = !this.#reconnecting && claimFirstVoiceOpen();
      socket.send(
        JSON.stringify({
          ...this.#screenSelection.activate(),
          greet: this.#greetClaimed,
          websiteContext: this.#options.getWebsiteContext?.(),
        }),
      );
    } catch (error) {
      const reason = error instanceof Error ? error : new Error(String(error));
      this.#rejectStart?.(reason);
      void this.stop();
    }
  }

  level(): number {
    return this.#media ? this.#media.inputLevel : this.#audio.level();
  }

  setMuted(muted: boolean): void {
    this.#muted = muted;
    this.#audio.setMuted(muted);
    this.#media?.setMuted(muted);
    if (this.#liveProtocol)
      this.#sendControlEvent({ type: 'voice.control', action: muted ? 'mute' : 'unmute' });
  }

  updateScreen(selection: BrowserVoiceScreenSelection): void {
    const event = this.#screenSelection.update(selection);
    if (event && this.#socket?.readyState === WebSocket.OPEN) {
      this.#socket.send(JSON.stringify(event));
    }
  }

  updateWebsiteContext(context: Readonly<Record<string, string | number | boolean | null>>): void {
    this.#sendControlEvent({ type: 'voice.context', websiteContext: context });
  }

  stop(): Promise<void> {
    this.#stopPromise ??= this.#stop();
    return this.#stopPromise;
  }

  async #stop(): Promise<void> {
    this.#stopped = true;
    const mediaStopped = this.#media?.stop();
    this.#active = false;
    clearTimeout(this.#livenessTimer);
    this.#recovery.stop();
    clearTimeout(this.#activationTimer);
    this.#rejectStart?.(new DOMException('Voice setup was cancelled', 'AbortError'));
    this.#rejectStart = null;
    this.#resolveStart = null;
    if (this.#meterId !== null) {
      cancelAnimationFrame(this.#meterId);
      this.#meterId = null;
    }
    realtimeVoiceLevels.input = 0;
    realtimeVoiceLevels.output = 0;
    if (this.#socket) {
      if (this.#socket.readyState === WebSocket.OPEN) {
        this.#socket.send(JSON.stringify({ type: 'voice.control', action: 'end' }));
      }
      if (this.#socket.readyState < WebSocket.CLOSING) {
        this.#socket.close();
      }
    }
    this.#socket = null;
    await Promise.all([mediaStopped, this.#audio.stop()]);
    this.#options.onState?.('idle');
    this.#options.onClose?.();
  }

  /**
   * Starts the attachment protocol once the socket opens. The server does not
   * admit microphone audio until it has configured the provider and seeded
   * durable session context.
   */
  #sendInitialize(socket: WebSocket): void {
    socket.send(
      JSON.stringify({ type: 'voice.initialize', memories: this.#options.getMemories?.() ?? [] }),
    );
  }

  #liveCaption(
    fragment: Pick<LiveBrowserTranscript, 'speaker' | 'delta' | 'startMs' | 'endMs'>,
  ): void {
    if (fragment.speaker === 'user') {
      this.#options.onUserCaption?.(fragment.delta);
      return;
    }
    const startsNew =
      this.#assistantCaptionEndMs < 0 || fragment.startMs > this.#assistantCaptionEndMs + 1_000;
    this.#assistantCaptionEndMs = Math.max(this.#assistantCaptionEndMs, fragment.endMs);
    this.#options.onAssistantCaption?.(fragment.delta, startsNew);
  }

  #handleServerEvent(raw: string): void {
    let event: unknown;
    try {
      event = JSON.parse(raw);
    } catch {
      return;
    }
    if (!isRecord(event) || typeof event['type'] !== 'string') {
      return;
    }
    switch (event['type']) {
      case 'voice.context_ready':
        this.#liveProtocol = event['protocol'] === 'live';
        if (this.#liveProtocol) {
          if (event['transport'] !== 'webrtc') {
            this.#options.onError?.('Voice server needs an update. Please reload later.');
            void this.stop();
            return;
          }
          this.#media ??= new BrowserLiveMedia({
            transcript: (fragment) => this.#liveCaption(fragment),
            failed: (message, retryable) => {
              if (retryable) this.#reconnect();
              else {
                this.#options.onError?.(message);
                void this.stop();
              }
            },
          });
        }
        if (this.#socket) {
          void this.#prepareAudio(this.#socket);
        }
        return;
      case 'voice.webrtc.answer': {
        const socket = this.#socket;
        if (
          !this.#media ||
          !socket ||
          typeof event['sessionId'] !== 'string' ||
          typeof event['sdp'] !== 'string'
        )
          return;
        void this.#media
          .acceptAnswer(event['sessionId'], event['sdp'])
          .then(() => {
            if (this.#socket !== socket || this.#stopped) return;
            this.#activationSent = true;
            this.#greetClaimed = !this.#reconnecting && claimFirstVoiceOpen();
            socket.send(
              JSON.stringify({
                ...this.#screenSelection.activate(),
                greet: this.#greetClaimed,
                websiteContext: this.#options.getWebsiteContext?.(),
              }),
            );
          })
          .catch(() => {
            if (this.#socket === socket) this.#reconnect();
          });
        return;
      }
      case 'voice.active':
        if (!this.#active) {
          this.#media?.activate();
          this.#active = true;
          clearTimeout(this.#activationTimer);
          this.#recovery.ready();
          if (this.#muted) this.setMuted(true);
          if (this.#reconnecting) this.#greetClaimed = false;
          this.#startMeter();
          // Live can listen immediately; a requested greeting does not prove work is pending.
          const awaitingLegacyGreeting = this.#greetClaimed && !this.#liveProtocol;
          this.#options.onState?.(awaitingLegacyGreeting ? 'thinking' : 'listening');
          this.#resolveStart?.();
          this.#resolveStart = null;
          this.#rejectStart = null;
        }
        return;
      case 'voice.audio':
        if (
          this.#liveProtocol &&
          !this.#media &&
          typeof event['delta'] === 'string' &&
          typeof event['sequence'] === 'number' &&
          Number.isSafeInteger(event['sequence']) &&
          event['sequence'] > this.#lastSequence
        ) {
          this.#lastSequence = event['sequence'];
          try {
            const chunkId = `live-chunk:${this.#lastSequence}`;
            this.#audio.enqueuePlayback(chunkId, 0, event['delta']);
            this.#audio.markPlaybackInputDone(chunkId);
          } catch (error) {
            this.#options.onError?.(
              error instanceof Error ? error.message : 'Voice playback failed',
            );
            void this.stop();
          }
        }
        return;
      case 'voice.transcript':
        // Native WebRTC captions arrive only on the restricted provider data channel.
        if (
          !this.#media &&
          typeof event['delta'] === 'string' &&
          (event['speaker'] === 'user' || event['speaker'] === 'assistant') &&
          typeof event['startMs'] === 'number' &&
          typeof event['endMs'] === 'number'
        ) {
          this.#liveCaption({
            speaker: event['speaker'],
            delta: event['delta'],
            startMs: event['startMs'],
            endMs: event['endMs'],
          });
        }
        return;
      case 'response.output_audio.delta':
        if (
          typeof event['delta'] === 'string' &&
          typeof event['item_id'] === 'string' &&
          typeof event['content_index'] === 'number'
        ) {
          this.#audio.enqueuePlayback(event['item_id'], event['content_index'], event['delta']);
          this.#options.onState?.('speaking');
        }
        return;
      case 'response.output_audio.done':
        if (typeof event['item_id'] === 'string') {
          this.#audio.markPlaybackInputDone(event['item_id']);
        }
        return;
      case 'input_audio_buffer.speech_started':
        {
          const interruption = this.#audio.clearPlayback();
          for (const itemId of interruption.completedItemIds) {
            this.#sendControlEvent({ type: 'voice.playback.completed', itemId });
          }
          for (const truncation of interruption.truncations) {
            this.#sendControlEvent({
              type: 'voice.playback.truncate',
              itemId: truncation.itemId,
              contentIndex: truncation.contentIndex,
              audioEndMs: truncation.audioEndMs,
            });
          }
        }
        this.#options.onState?.('listening');
        return;
      case 'input_audio_buffer.speech_stopped':
        // VAD closed the visitor's utterance — the model is now processing it.
        this.#options.onState?.('thinking');
        return;
      case 'response.output_audio_transcript.delta':
        if (typeof event['delta'] === 'string') {
          this.#options.onAssistantCaption?.(event['delta']);
        }
        return;
      case 'conversation.item.input_audio_transcription.delta':
        if (typeof event['delta'] === 'string') {
          this.#options.onUserCaption?.(event['delta']);
        }
        return;
      case 'voice.state':
        if (isVoiceRealtimeState(event['state'])) {
          this.#options.onState?.(event['state']);
        }
        return;
      case 'voice.run':
        if (typeof event['busy'] === 'boolean') {
          const responseId = event['responseId'];
          this.#options.onRunBusy?.(
            event['busy'],
            typeof responseId === 'string' && responseId.length > 0 ? responseId : undefined,
          );
        }
        return;
      case 'voice.closed':
        this.#terminal = event['retryable'] !== true;
        return;
      case 'voice.error':
        this.#terminal = true;
        this.#options.onError?.(typeof event['message'] === 'string' ? event['message'] : '');
        return;
      case 'voice.memory': {
        const update = readVisitorContextUpdate(event);
        if (update) {
          const memories = this.#options.onMemory?.(update);
          if (memories) {
            this.#sendControlEvent({ type: 'voice.initialize', memories });
          }
        }
        return;
      }
      default:
        return;
    }
  }

  #sendControlEvent(event: Record<string, unknown>): void {
    if (this.#socket?.readyState === WebSocket.OPEN) {
      this.#socket.send(JSON.stringify(event));
    }
  }
}

function isVoiceRealtimeState(value: unknown): value is VoiceRealtimeState {
  return (
    value === 'idle' ||
    value === 'connecting' ||
    value === 'listening' ||
    value === 'thinking' ||
    value === 'speaking' ||
    value === 'building'
  );
}

/** Session wired to AgentAuth for the agent session id — mirrors `createWebSocketClient`. */
export function createVoiceRealtimeSession(
  options: Omit<VoiceRealtimeSessionOptions, 'getAgentSessionId'>,
): VoiceRealtimeSession {
  return new VoiceRealtimeSession({
    ...options,
    getAgentSessionId: () => AgentAuth.agentSessionId,
  });
}
